import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  ButtonGroup,
  Button,
  Drawer,
  useMediaQuery,
  useTheme,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  TablePagination,
} from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  FilterList,
  Refresh,
  Send,
  SensorsOff,
  WarningAmberOutlined,
} from '@mui/icons-material';

import { useButtonEvents, QUICK_FILTER_LABELS, QuickFilter } from './hooks/useButtonEvents';
import { COLORS, STATUS_CONFIG } from './_components/Constants';
import FiltersSidebar from './_components/FiltersSidebar';
import { ISportButtonEventProps } from '../../../services/Sports/ButtonEvents/types';
import SyncActiveToast from '../../../components/SyncActiveToast';
import ProgressDialog from '../../../components/ProgressDialog';

const {
  backgroundColorDarkTheme,
  tableBackgroundColor,
  tableHeaderColor,
  borderColorDarkTheme,
  fontColorDarkTheme,
} = COLORS;

const formatDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString('pt-BR') : '-');

interface StatCardProps {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
}

const StatCard = ({ label, value, color, icon }: StatCardProps) => (
  <Box
    sx={{
      flex: '1 1 160px',
      minWidth: 160,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 2,
      borderRadius: 3,
      backgroundColor: tableBackgroundColor,
      border: `1px solid ${borderColorDarkTheme}`,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${color}22`,
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'gray', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const SportButtonEventsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { state, actions } = useButtonEvents();

  const sidebar = (
    <FiltersSidebar
      devices={state.devices}
      deviceId={state.filters.deviceId}
      status={state.filters.status}
      search={state.filters.search}
      startDate={state.filters.startDate}
      endDate={state.filters.endDate}
      isLoading={state.isLoading || state.isFetching}
      onDeviceChange={actions.setDeviceId}
      onStatusToggle={(status) => actions.setStatus((prev) => (prev === status ? undefined : status))}
      onSearchChange={actions.setSearch}
      onStartDateChange={(date) => {
        actions.setStartDate(date);
        actions.setActiveQuickFilter('personalizado');
      }}
      onEndDateChange={(date) => {
        actions.setEndDate(date);
        actions.setActiveQuickFilter('personalizado');
      }}
      onApply={actions.applyFilters}
      onClear={actions.handleClearFilters}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
        backgroundColor: backgroundColorDarkTheme,
        color: fontColorDarkTheme,
      }}
    >
      {isMobile ? (
        <Drawer
          anchor="left"
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          slotProps={{ paper: { sx: { width: 260, backgroundColor: tableHeaderColor } } }}
        >
          {sidebar}
        </Drawer>
      ) : (
        <Box sx={{ width: 260, flexShrink: 0, borderRight: `1px solid ${borderColorDarkTheme}`, backgroundColor: tableHeaderColor }}>
          {sidebar}
        </Box>
      )}

      <Box sx={{ flex: 1, p: 4, overflowY: 'auto' }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Eventos de Acionamento
              </Typography>
              <Typography variant="body2" sx={{ color: 'gray' }}>
                Auditoria dos acionamentos do módulo físico (botão RF) e reenvio para a API de esportes.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {isMobile && (
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setIsFilterOpen(true)}
                  sx={{ textTransform: 'none', borderColor: borderColorDarkTheme, color: 'gray' }}
                >
                  Filtros
                </Button>
              )}

              <ButtonGroup variant="outlined" size="small" sx={{ '& .MuiButton-root': { borderColor: borderColorDarkTheme, color: 'gray', textTransform: 'none' } }}>
                {(['hoje', '7d', 'todos'] as QuickFilter[]).map((preset) => (
                  <Button
                    key={preset}
                    onClick={() => actions.handleQuickDateFilter(preset)}
                    sx={{
                      backgroundColor: state.filters.activeQuickFilter === preset ? 'rgba(88, 166, 255, 0.1)' : 'transparent',
                      color: state.filters.activeQuickFilter === preset ? '#58a6ff !important' : 'inherit',
                    }}
                  >
                    {QUICK_FILTER_LABELS[preset]}
                  </Button>
                ))}
              </ButtonGroup>

              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={actions.handleRefresh}
                disabled={state.isFetching}
                sx={{ textTransform: 'none' }}
              >
                Atualizar
              </Button>
            </Box>
          </Box>

          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 2 }}>
            <StatCard label="Download OK" value={state.statusCounts.success} color="#4caf50" icon={<CheckCircleOutline fontSize="small" />} />
            <StatCard label="Falha no download" value={state.statusCounts.sendFailed} color="#f44336" icon={<ErrorOutline fontSize="small" />} />
            <StatCard label="Dispositivo não vinculado" value={state.statusCounts.deviceNotFound} color="#ff9800" icon={<SensorsOff fontSize="small" />} />
            <StatCard label="Payload inválido" value={state.statusCounts.invalidPayload} color="#ffc107" icon={<WarningAmberOutlined fontSize="small" />} />
          </Stack>

          {state.selected.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Send />}
                onClick={actions.handleResendSelected}
                disabled={state.isResending}
                sx={{ textTransform: 'none' }}
              >
                Reenviar selecionados ({state.selected.length})
              </Button>
            </Box>
          )}

          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: tableBackgroundColor,
              borderRadius: 3,
              boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
              border: `1px solid ${borderColorDarkTheme}`,
              overflowX: 'auto',
            }}
          >
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 900, borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ backgroundColor: tableHeaderColor, borderBottom: `1px solid rgba(255,255,255,0.1)` }}>
                    <Checkbox
                      checked={state.events.length > 0 && state.selected.length === state.events.length}
                      indeterminate={state.selected.length > 0 && state.selected.length < state.events.length}
                      onChange={actions.toggleSelectAll}
                      sx={{ color: 'gray' }}
                    />
                  </TableCell>
                  {[
                    { label: 'Data/Hora', width: '16%' },
                    { label: 'Dispositivo', width: '20%' },
                    { label: 'ID Controle', width: '14%' },
                    { label: 'Status', width: '14%' },
                    { label: 'Último Reenvio', width: '16%' },
                    { label: 'Reenvios', width: '8%', align: 'center' as const },
                    { label: 'Ações', width: '12%', align: 'right' as const },
                  ].map((col) => (
                    <TableCell
                      key={col.label}
                      align={col.align || 'left'}
                      sx={{ width: col.width, backgroundColor: tableHeaderColor, color: 'gray', fontWeight: 'bold', borderBottom: `1px solid rgba(255,255,255,0.1)` }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {state.isLoading || state.isFetching ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, borderBottom: 'none' }}>
                      <CircularProgress size={30} sx={{ color: fontColorDarkTheme }} />
                    </TableCell>
                  </TableRow>
                ) : state.events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: fontColorDarkTheme, borderBottom: 'none' }}>
                      Nenhum evento encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  state.events.map((event: ISportButtonEventProps) => {
                    const statusCfg = STATUS_CONFIG[event.status] ?? { label: event.status, color: 'default' as any };
                    const isSelected = state.selected.includes(event.id);
                    return (
                      <TableRow
                        key={event.id}
                        selected={isSelected}
                        hover
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: 'rgba(255,255,255,0.02)' },
                          transition: 'background-color 0.15s',
                        }}
                      >
                        <TableCell padding="checkbox" sx={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          <Checkbox checked={isSelected} onChange={() => actions.toggleSelected(event.id)} sx={{ color: 'gray' }} />
                        </TableCell>
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {formatDateTime(event.createdAt)}
                        </TableCell>
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {event.deviceName || 'Não vinculado'}
                        </TableCell>
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {event.idControle}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          <Tooltip title={event.errorMessage || ''}>
                            <Chip size="small" label={statusCfg.label} color={statusCfg.color} sx={{ borderRadius: 1.5 }} />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {formatDateTime(event.lastSentAt)}
                        </TableCell>
                        <TableCell align="center" sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {event.resendCount}
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          <Tooltip title="Reenviar para a API de esportes">
                            <IconButton size="small" onClick={() => actions.handleResendOne(event.id)} disabled={state.isResending}>
                              <Send fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={state.totalCount}
              page={state.pagination.pageNumber - 1}
              rowsPerPage={state.pagination.pageSize}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={actions.handleChangePage}
              onRowsPerPageChange={actions.handleChangeRowsPerPage}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{ color: fontColorDarkTheme, borderTop: `1px solid ${borderColorDarkTheme}` }}
            />
          </TableContainer>
        </Box>
      </Box>

      {state.resendJob && (
        <>
          <SyncActiveToast
            open={state.isResendToastOpen}
            personName="Reenvio de eventos"
            progress={state.resendJob.progress}
            statusMessage={state.resendJob.message ?? undefined}
            success={state.resendJob.status === 'Completed'}
            successLingerMs={5000}
            operation="update"
            doneMessageOverride={state.resendJob.message ?? undefined}
            onView={actions.openResendDialog}
            onClose={actions.closeResendToast}
          />

          <ProgressDialog
            open={state.isResendDialogOpen}
            onClose={actions.closeResendDialog}
            onMinimize={actions.minimizeResendDialog}
            progress={state.resendJob.progress}
            status={
              state.resendJob.status === 'Completed'
                ? 'success'
                : state.resendJob.status === 'Failed'
                  ? 'error'
                  : state.resendJob.status === 'Canceled'
                    ? 'canceled'
                    : 'loading'
            }
            statusMessage={state.resendJob.message ?? 'Aguardando...'}
            title="Reenvio de Eventos"
            maxWidth="xs"
          />
        </>
      )}
    </Box>
  );
};

export default SportButtonEventsPage;
